import React, { useMemo, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import thLocale from "@fullcalendar/core/locales/th";
import AppointmentDetailModal from "../Modal/AppointmentDetailModal";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
import FullPageModal from "../Modal/FullCustomerModal";
import Page from "../../pages/Sale/index";
import CallModal from "../Calendar/CallModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";

export default function CalendarWrapper({ appointments = [], refreshAppointments }) {
  const calendarRef = useRef(null);

  const [openPageModal, setOpenPageModal] = useState(false);   // ←✔ moved here
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [calendarAction, setCalendarAction] = useState(null);
  const [calendarActionDate, setCalendarActionDate] = useState(null);
  const [selectedDateForPage, setSelectedDateForPage] = useState(null);
  const [openCallModal, setOpenCallModal] = useState(false);
  const [callData, setCallData] = useState(null);
  const [selectedButtonType, setSelectedButtonType] = useState(null);


  const resources = useMemo(() => {
    const map = new Map();
    appointments.forEach((app) => {
      if (!app.Sale_Emp_Name) return;
      if (!map.has(app.Sale_Emp_Name)) {
        map.set(app.Sale_Emp_Name, {
          id: app.Sale_Emp_Name,
          title: app.Sale_Emp_Name,
        });
      }
    });
    return [...map.values()];
  }, [appointments]);

  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    return dateStr.split("T")[0];   // ดึงเฉพาะ YYYY-MM-DD
  };


 const combineDateAndTime = (dateStr, timeStr) => {
    if (!dateStr) return null;

    // sanitize input
    const cleanDate = normalizeDate(dateStr);

    const [y, m, d] = cleanDate.split("-");
    const [hh, mm] = (timeStr || "00:00").split(":");

    return new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      0
    );
  };



  const events = useMemo(() => {
    const result = [];

    appointments.forEach((app) => {
      if (app.Appoint_Id) {
        let color = "#3b82f6";
        let distanceKm = null;
        let distanceStatus = "NOT_USED";

        if (app.SpecRemark && app.SpecRemark.trim() !== "") {
          color = "#4ade80";
          if (
            app.Sale_Latitude &&
            app.Sale_Longitude &&
            app.SA_Latitude &&
            app.SA_Longitude
          ) {
            distanceKm = getDistanceKm(
              parseFloat(app.Sale_Latitude),
              parseFloat(app.Sale_Longitude),
              parseFloat(app.SA_Latitude),
              parseFloat(app.SA_Longitude)
            );
            if (distanceKm > 1) {
              color = "#f87171";
              distanceStatus = "OUT_RANGE";
            } else {
              distanceStatus = "IN_RANGE";
            }
          }
        }

        const startDT = combineDateAndTime(normalizeDate(app.Appoint_Date), app.Time_Start);
        const endDT   = combineDateAndTime(normalizeDate(app.Appoint_Date), app.Time_End);


        result.push({
          id: `appoint-${app.Appoint_Id}`,
          resourceId: app.Sale_Emp_Name,
          title: `${app.AR_Name || "-"} ${app.AR_LastName || ""}`.trim(),
          start: startDT,
          end: endDT,
          allDay: !app.Time_Start,
          backgroundColor: color,
          borderColor: color,
          textColor: "#fff",
          extendedProps: {
            ...app,
            EventType: "APPOINT",
            DistanceKm: distanceKm,
            DistanceStatus: distanceStatus,
          },
        });
      }

      if (app.Call_Id) {
        const hasResult = !!app.Call_Result;
        const bg = hasResult ? "#E9D5FF" : "#DBEAFE";
        const iconColor = hasResult ? "#9333EA" : "#1D4ED8";

        // ดึงเฉพาะวันที่ (ไม่ใช้ new Date)
        const dateOnly = normalizeDate(app.Call_Date);

        let startDT = null;
        let endDT = null;

        if (app.Call_Type === 2 && !hasResult) {
          // followup ยังไม่มีผล → แสดงเป็น range ปกติ
          const ts = app.Time_Start || "09:00";
          const te = app.Time_End || "09:30";

          startDT = combineDateAndTime(dateOnly, ts);
          endDT = combineDateAndTime(dateOnly, te);
        } else {
          // มีผลการโทรแล้ว หรือเป็น call ปกติ → แสดงเวลาเดียว
          const ts = app.Time_Start || "09:00";
          const te = app.Time_End || "09:30";

          startDT = combineDateAndTime(dateOnly, ts);
          endDT = combineDateAndTime(dateOnly, te);
        }

        result.push({
          id: `call-${app.Call_Id}`,
          resourceId: app.Sale_Emp_Name,
          title: `📞 ${app.AR_Name || "-"} ${app.AR_LastName || ""}`.trim(),
          start: startDT,
          end: endDT,
          allDay: false,
          backgroundColor: bg,
          borderColor: bg,
          textColor: iconColor,
          extendedProps: {
              ...app,
              EventType: "CALL",
              HasCallResult: hasResult,
              IconColor: iconColor,
              Call_DateRaw: app.Call_Date,
          },
        });
      }

    });

    return result;
  }, [appointments]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .fc-daygrid-event {
        overflow: hidden !important;
        white-space: nowrap !important;
        text-overflow: ellipsis !important;
        max-width: 100% !important;
      }

      .fc-popover { 
        max-height: 260px !important;
        overflow: visible !important;
        z-index: 1000 !important;
      }

      .fc-popover-body {
        max-height: 230px !important;
        overflow-y: auto !important;
      }

      .fc-timegrid-slot { height: 36px !important; }
      .fc { z-index: 1 !important; }
    `;

    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSaveCall = async (mode, payload, callId) => {
    try {
      if (mode === "edit" && callId) {
        await api.put(`/update-call-info/${callId}`, {
          ...payload,
          Update_By_Name: "System",
          Update_By_Id: 0,
        });
      } else {
        await api.post(`/create-call`, {
          ...payload,
          Update_By_Name: "System",
          Update_By_Id: 0,
        });
      }

      setOpenCallModal(false);

      // Swal.fire("สำเร็จ", "บันทึกข้อมูลการโทรแล้ว", "success");

      window.location.reload();
    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด", "บันทึกข้อมูลการโทรไม่สำเร็จ", "error");
    }
  };


  return (
    <div className="p-4">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, resourceTimeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale={thLocale}
        events={events}
        resources={resources}
        height="80vh"
        eventDisplay="block"
        dayMaxEventRows={3}
        moreLinkClick="popover"
        dayCellDidMount={(arg) => {
          const cell = arg.el;
          const d = arg.date;
          const dateStr = d.toLocaleDateString("sv-SE"); 


          const handleCellClick = (e) => {
            const isIcon =
              e.target.closest(".fc-btn-call") ||
              e.target.closest(".fc-btn-appoint");
            const isMoreLink = e.target.closest(".fc-more-link");
            const isPopover = e.target.closest(".fc-popover");
            const isEvent = e.target.closest(".fc-event");

            if (isIcon || isMoreLink || isPopover || isEvent) {
              return;
            }

            calendarRef.current
              ?.getApi()
              .changeView("resourceTimeGridDay", dateStr);
          };

          cell.addEventListener("click", handleCellClick);

          const callBtn = cell.querySelector(".fc-btn-call");
          const appointBtn = cell.querySelector(".fc-btn-appoint");

          if (appointBtn) {
            appointBtn.addEventListener("click", (e) => {
              e.stopPropagation();
              setSelectedDateForPage(dateStr);
              setSelectedButtonType("appoint");
              setOpenPageModal(true);
            });
          }

          if (callBtn) {
            callBtn.addEventListener("click", (e) => {
              e.stopPropagation();
              setSelectedDateForPage(dateStr);
              setSelectedButtonType("call");
              setOpenPageModal(true);
            });
          }


        }}
        dayCellContent={(arg) => {
          const d = arg.date;
          const dateStr = d.toLocaleDateString("sv-SE");

          return {
            html: `
              <div style="display:flex; justify-content:space-between; align-items:center; width:100%; padding:2px 4px;">
                <span class="fc-daygrid-day-number">${arg.dayNumberText}</span>
                <span style="display:flex; gap:6px;">
                  <span class="fc-btn-call" data-date="${dateStr}" style="cursor:pointer; font-size:18px;">📞</span>
                  <span class="fc-btn-appoint" data-date="${dateStr}" style="cursor:pointer; font-size:18px;">📅</span>
                </span>
              </div>
            `,
          };
        }}
        eventContent={(arg) => {
          const p = arg.event.extendedProps;
          if (p.EventType === "CALL") {
            return {
              html: `
                <div class="fc-event-main" style="display:flex; gap:4px; align-items:center; font-size:14px;">
                  <span style="font-size:18px; color:${p.IconColor};">📞</span>
                  <span style="font-size:14px; color:${p.IconColor};">${arg.event.title
                    .replace("📞", "")
                    .trim()}</span>
                </div>
              `,
            };
          }
          return {
            html: `
              <div class="fc-event-main" style="display:flex; align-items:center; font-size:14px;">
                ${arg.event.title}
              </div>
            `,
          };
        }}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,resourceTimeGridDay",
        }}
        views={{
          dayGridMonth: { buttonText: "เดือน" },
          resourceTimeGridDay: {
            buttonText: "วัน",
            slotMinTime: "00:00:00",
            slotMaxTime: "24:00:00",
            slotDuration: "01:00:00",
            slotLabelContent: (arg) =>
              `${String(arg.date.getHours()).padStart(2, "0")}.00`,
          },
        }}
        displayEventTime={false}
        dateClick={null}
        eventClick={(info) => {
          const p = info.event.extendedProps;

          if (p.EventType === "CALL") {

              const payload = {
                Call_Id: p.Call_Id,
                AR_Id: p.AR_Id,
                ServiceAddr_Id: p.ServiceAddr_Id,

                Sale_Emp_Id: p.Sale_Emp_Id,
                Sale_Emp_Name:
                  p.Sale_Emp_Name ||
                  p.Emp_Name ||
                  p.EmployeeName ||
                  p.SalesName ||
                  "",

                Call_Date: p.Call_DateRaw,  // ISO string ดิบ
                Time_Start: p.Time_Start || "",
                Time_End: p.Time_End || "",
                Call_Result: p.Call_Result || "",
                Call_Remark: p.Call_Remark || "",
              };

              setCallData(payload);
              setOpenCallModal(true);
              return;
          }





          setCalendarAction(null);
          setCalendarActionDate(null);
          setSelectedAppointment(p);
          setOpenModal(true);
        }}


      />

      {/* 🔎 Legend อธิบายสี */}
      <div className="mt-3 p-3 bg-white  text-sm flex flex-wrap gap-6">

        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#3b82f6" }}></div>
          <span>นัดหมายเพื่อเข้าพบ (ยังไม่มีผลการเข้าพบ)</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#4ade80" }}></div>
          <span>นัดหมายเพื่อเข้าพบ (มีผลการเข้าพบแล้ว)</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#f87171" }}></div>
          <span>นัดหมายเพื่อเข้าพบ (นอกระยะทำการ)</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#1D4ED8" }}></div>
          <span>นัดหมายเพื่อโทร (ยังไม่มีผลการโทร)</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#9333EA" }}></div>
          <span>นัดหมายเพื่อโทร (มีผลการโทรแล้ว)</span>
        </div>



      </div>


      {openModal && (
        <AppointmentDetailModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          mode={calendarAction}
          date={calendarActionDate}
          appoint={selectedAppointment}
          onUpdated={() => window.location.reload()}
        />
      )}
      {openPageModal && (
        <FullPageModal
          isOpen={openPageModal}
          onClose={async () => {
            setOpenPageModal(false);
            if (refreshAppointments) {
              await refreshAppointments();
            }
          }}

        >
          <Page 
            embedded={true} 
            selectedDate={selectedDateForPage} 
            buttonType={selectedButtonType} 
          />
        </FullPageModal>
      )}

      {openCallModal && (
        <CallModal
          isOpen={openCallModal}
          onClose={() => setOpenCallModal(false)}
          initialData={callData}
          onSave={handleSaveCall}
        />
      )}


    </div>
  );
}

function getDistanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
